class DataService {
  constructor() {
    this.data = null;
    this.summaryData = null;
  }

  async loadData() {
    try {
      // Look for the latest comprehensive data file
      // In a real app, this would be an API call
      // For now, we'll simulate loading from the JSON file
      const response = await fetch('/zerodha_comprehensive_data.json');
      if (!response.ok) {
        throw new Error('Failed to load data');
      }
      this.data = await response.json();
      this.summaryData = this.createSummaryData(this.data);
      return this.data;
    } catch (error) {
      console.error('Error loading data:', error);
      // Fallback to mock data for development
      return this.getMockData();
    }
  }

  createSummaryData(data) {
    const segments = ['FO', 'EQ', 'MF', 'CDS', 'COM'];
    const summaryData = [];
    
    // Track previous year's unrealized P&L to calculate CHANGE (not absolute)
    // This prevents double-counting when positions are held across years
    const prevUnrealized = {};
    segments.forEach(segment => {
      prevUnrealized[segment] = 0;
    });

    // Sort financial years by year
    const sortedFYs = Object.entries(data.financial_years).sort(
      (a, b) => a[1].fy_info.year - b[1].fy_info.year
    );

    for (const [fyLabel, fyData] of sortedFYs) {
      const fyTotals = {
        fy: fyLabel,
        year: fyData.fy_info.year,
      };

      // Initialize segment totals
      segments.forEach(segment => {
        fyTotals[`${segment}_realized`] = 0;
        fyTotals[`${segment}_unrealized`] = 0;
        fyTotals[`${segment}_unrealized_change`] = 0;
        fyTotals[`${segment}_total_pnl`] = 0;
        fyTotals[`${segment}_charges`] = 0;
      });

      let totalRealized = 0;
      let totalUnrealizedChange = 0;
      let totalCharges = 0;

      // Process each segment
      for (const [segmentCode, segmentInfo] of Object.entries(fyData.segments)) {
        if (segmentInfo.data && segmentInfo.data.data && segmentInfo.data.data.result) {
          try {
            const result = segmentInfo.data.data.result;
            const pnlData = result.pnl || {};
            const chargesData = result.charges || [];
            const otherCharges = result.other_charges || 0;

            const realized = pnlData.realized_profit || 0;
            const currentUnrealized = pnlData.unrealized_profit || 0;
            
            // Calculate CHANGE in unrealized P&L from previous year
            // This year's contribution = current unrealized - previous year's unrealized
            // Example: If MF was 15L last year, 45L this year, this year's gain is 30L
            const unrealizedChange = currentUnrealized - (prevUnrealized[segmentCode] || 0);
            
            const segmentCharges = chargesData.reduce((sum, charge) => 
              sum + Math.abs(charge.tax_amount || 0), 0) + Math.abs(otherCharges);

            // Total P&L = Realized + Change in Unrealized
            fyTotals[`${segmentCode}_realized`] = realized;
            fyTotals[`${segmentCode}_unrealized`] = currentUnrealized;
            fyTotals[`${segmentCode}_unrealized_change`] = unrealizedChange;
            fyTotals[`${segmentCode}_total_pnl`] = realized + unrealizedChange;
            fyTotals[`${segmentCode}_charges`] = segmentCharges;

            totalRealized += realized;
            totalUnrealizedChange += unrealizedChange;
            totalCharges += segmentCharges;
            
            // Update for next year's calculation
            prevUnrealized[segmentCode] = currentUnrealized;
          } catch (error) {
            console.warn(`Error processing ${segmentCode} data for ${fyLabel}:`, error);
          }
        }
      }

      // Add overall totals
      fyTotals.total_realized = totalRealized;
      fyTotals.total_unrealized_change = totalUnrealizedChange;
      fyTotals.total_pnl = totalRealized + totalUnrealizedChange;
      fyTotals.total_charges = totalCharges;
      fyTotals.net_pnl = totalRealized + totalUnrealizedChange - totalCharges;

      summaryData.push(fyTotals);
    }

    return summaryData;
  }

  getMockData() {
    // Mock data for development/demo
    return {
      fetch_timestamp: new Date().toISOString(),
      segments_info: {
        'FO': 'Futures & Options',
        'EQ': 'Equity',
        'MF': 'Mutual Funds',
        'CDS': 'Currency Derivatives',
        'COM': 'Commodities'
      },
      financial_years: {
        'FY 2019-20': {
          fy_info: { label: 'FY 2019-20', year: 2019 },
          segments: {
            'FO': { data: { data: { result: { pnl: { realized_profit: 100000, unrealized_profit: 50000 }, charges: [] } } } },
            'EQ': { data: { data: { result: { pnl: { realized_profit: 200000, unrealized_profit: 100000 }, charges: [] } } } },
            'MF': { data: { data: { result: { pnl: { realized_profit: -5000, unrealized_profit: 550000 }, charges: [] } } } }
          }
        },
        // Add more mock years...
      }
    };
  }

  getSummaryData() {
    return this.summaryData || [];
  }

  getLastUpdated() {
    return this.data?.last_updated || null;
  }

  getDaysAgo() {
    const lastUpdated = this.getLastUpdated();
    if (!lastUpdated) return null;
    
    const now = new Date();
    const updated = new Date(lastUpdated.timestamp);
    const diffTime = Math.abs(now - updated);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "today";
    if (diffDays === 1) return "1 day ago";
    return `${diffDays} days ago`;
  }

  // Shared metrics calculation for both dashboard and homepage
  getSharedMetrics() {
    const summaryData = this.getSummaryData();
    if (!summaryData || summaryData.length === 0) return null;

    // Initial capital in FY 2019-20 = ₹10L
    const INITIAL_CAPITAL = 1000000; // 10 Lakhs

    // Sort by year (ascending)
    const sortedData = [...summaryData].sort((a, b) => a.year - b.year);
    
    // Calculate capital at the START of each year using TOTAL P&L (including MF)
    // This represents actual account value which includes all segments
    const capitalByYear = {};
    let runningCapital = INITIAL_CAPITAL;
    
    for (let i = 0; i < sortedData.length; i++) {
      const yearData = sortedData[i];
      capitalByYear[yearData.fy] = runningCapital; // Capital at START of this year
      runningCapital += yearData.net_pnl;  // Add TOTAL P&L for next year
    }
    
    const finalCapital = runningCapital;
    const totalYears = sortedData.length;

    // Calculate CAGR based on total returns (entire portfolio)
    let cagr = 0;
    if (totalYears > 0 && finalCapital > 0) {
      cagr = (Math.pow(finalCapital / INITIAL_CAPITAL, 1 / totalYears) - 1) * 100;
    }

    // Find best year based on TOTAL P&L (all segments)
    // This shows the best performing year overall
    const bestYear = sortedData.length > 0 ? 
      sortedData.reduce((best, current) => 
        current.net_pnl > best.net_pnl ? current : best
      ) : null;
    
    const bestYearCapital = bestYear ? (capitalByYear[bestYear.fy] || INITIAL_CAPITAL) : INITIAL_CAPITAL;

    return {
      cagr: cagr,
      totalYears: totalYears,
      bestYearPnL: bestYear ? bestYear.net_pnl : 0,
      bestYearFY: bestYear ? bestYear.fy : null,
      bestYearCapital: bestYearCapital
    };
  }

  getSegments() {
    return ['FO', 'EQ', 'MF', 'CDS', 'COM'];
  }

  getSegmentInfo() {
    return {
      'FO': 'Futures & Options',
      'EQ': 'Equity', 
      'MF': 'Mutual Funds',
      'CDS': 'Currency Derivatives',
      'COM': 'Commodities'
    };
  }

  formatCurrency(amount) {
    const absAmount = Math.abs(amount);
    const sign = amount < 0 ? '-' : '';
    
    if (absAmount >= 10000000) {
      return `${sign}₹${(absAmount / 10000000).toFixed(1)}Cr`;
    } else if (absAmount >= 100000) {
      return `${sign}₹${(absAmount / 100000).toFixed(1)}L`;
    } else if (absAmount >= 1000) {
      return `${sign}₹${(absAmount / 1000).toFixed(1)}K`;
    } else {
      return `${sign}₹${absAmount.toFixed(0)}`;
    }
  }

  calculateGrowthRate(current, previous) {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / Math.abs(previous)) * 100;
  }
}

const dataServiceInstance = new DataService();
export default dataServiceInstance;